import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import PeerTable from '../components/PeerTable';
import PeerCard from '../components/PeerCard';
import './VpnManager.css';

const ROLES_CLAIM = process.env.REACT_APP_AUTH0_ROLES_CLAIM || 'https://larbika.com/roles';

export default function VpnManager() {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const roles = user?.[ROLES_CLAIM] || [];
  const isAdmin = roles.includes('Admin');

  const fetchPeers = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch('/api/vpn/peers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setPeers(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchPeers();
    const interval = setInterval(fetchPeers, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchPeers]);

  const handleUpdate = async (peerId, data) => {
    const token = await getAccessTokenSilently();
    await fetch(`/api/vpn/peers/${peerId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await fetchPeers();
  };

  const onlineCount = peers.filter(p => p.online).length;

  return (
    <div className="vpn-manager">
      <header className="vpn-header">
        <div className="vpn-header-left">
          <img src="/images/logo.png" alt="LarbiConnect" className="header-logo" />
          <span className="role-badge">{isAdmin ? 'Administrator' : 'User'}</span>
        </div>
        <div className="vpn-header-right">
          <div className="stats">
            <span className="stat online">{onlineCount} online</span>
            <span className="stat total">{peers.length} peers</span>
          </div>
          <div className="user-info">
            {user?.picture && <img src={user.picture} alt="avatar" className="avatar" />}
            <span>{user?.email}</span>
          </div>
          <button className="btn-logout" onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
            Sign out
          </button>
        </div>
      </header>

      <main className="vpn-main">
        {loading && <p className="loading-text">Loading peers…</p>}
        {error && <p className="error-text">Error: {error}</p>}
        {!loading && !error && (
          isAdmin
            ? <PeerTable peers={peers} onUpdate={handleUpdate} />
            : <div className="peer-grid">
                {peers.length === 0
                  ? <p className="empty-text">No peers assigned to your account yet.</p>
                  : peers.map(p => <PeerCard key={p.id} peer={p} />)
                }
              </div>
        )}
      </main>
    </div>
  );
}
